import { t } from 'i18next';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createAction } from 'shared/util/utility';
import { addNotification } from '../components/toast';
import { BeforeInstallPromptEvent } from '../global';
import { State } from '../interface';
import { createLogger } from '../logger';

type StatusType = 'accepted' | 'dismissed';
const classToWatch = '.lp-install-pwa';
const usePwaPrompt = () => {
	const dispatch = useDispatch();
	const pwaReference = useSelector<State, BeforeInstallPromptEvent | null>((state) => state.common.pwaReference);
	const logger = createLogger('usePwaPrompt');
	const install = () =>
		new Promise<StatusType>(async (resolve, reject) => {
			if (!pwaReference) {
				reject(null);
				return;
			}
			await pwaReference.prompt();
			pwaReference.userChoice.then((choiceResult) => resolve(choiceResult.outcome));
		});

	const setReference = (value: BeforeInstallPromptEvent | null) => dispatch(createAction('SET_COMMON', { pwaReference: value }));

	useEffect(() => {
		if (/Google Inc/.test(navigator.vendor)) {
			const handlePrompt = (event: BeforeInstallPromptEvent) => setReference(event);
			window.addEventListener('beforeinstallprompt', handlePrompt);
			return () => window.removeEventListener('beforeinstallprompt', handlePrompt);
		}
	}, []);

	useEffect(() => {
		const handleClick = (event: MouseEvent) => {
			const target = event.target as HTMLElement;
			logger.log('Clicked class ' + classToWatch);
			if (target.closest(classToWatch) && pwaReference) {
				pwaReference.prompt();
				pwaReference.userChoice.then((result) => {
					if (result.outcome === 'accepted') {
						addNotification('info', t('hasInstalledPwa'));
					}
				});
			}
		};

		document.addEventListener('click', handleClick, true);
		return () => document.removeEventListener('click', handleClick, true);
	}, [pwaReference]);

	return {
		install,
		reset: () => setReference(null),
		canInstall: pwaReference,
	};
};

export default usePwaPrompt;
